import { matchRoute, type RouteName } from "@softmax/web-addresses";
import {
  adopted,
  pathsMatch,
  readinessFamily,
  type NavigationStart,
  type NavigationStage,
  type Visit,
} from "./navigation-readiness-policy";

type Address = { readonly path: string; readonly search: string };
type Target = Address & {
  readonly family: RouteName;
  readonly stages: readonly NavigationStage[];
};
type Transition =
  | { readonly kind: "unchanged" | "finish" }
  | { readonly kind: "redirect" | "start"; readonly target: Target };

function targetAt(address: Address): Target | null {
  const family = readinessFamily(matchRoute(address.path).name);
  const stages = family === "unknown" ? undefined : adopted[family];

  if (family === "unknown" || !stages) return null;

  return { ...address, family, stages };
}

export function committedTransition({
  current,
  path,
  search,
}: Address & { readonly current: Visit }): Transition {
  if (pathsMatch({ left: path, right: current.path }))
    return { kind: search === current.search ? "unchanged" : "finish" };

  if (pathsMatch({ left: path, right: current.sourcePath }))
    return { kind: search === current.sourceSearch ? "unchanged" : "finish" };

  const target = targetAt({ path, search });

  if (
    !target ||
    (current.family !== "play" && target.family !== current.family)
  )
    return { kind: "finish" };

  return { kind: "redirect", target };
}

function continuesVisit({
  current,
  navigation,
  localUpdate,
  target,
}: {
  readonly current: Visit | null;
  readonly navigation: NavigationStart["navigation"];
  readonly localUpdate: boolean;
  readonly target: Target;
}): boolean {
  if (!current || navigation !== "replace" || current.settled.has("content"))
    return false;

  const redirect =
    !localUpdate ||
    current.family === "play" ||
    matchRoute(current.path).name !== current.family;
  return (
    redirect && (current.family === "play" || target.family === current.family)
  );
}

export function startTransition({
  current,
  committed,
  url,
  navigation,
  load,
}: {
  readonly current: Visit | null;
  readonly committed: Address;
  readonly url: string;
  readonly navigation: NavigationStart["navigation"];
  readonly load: Visit["load"];
}): Transition {
  const destination = new URL(url, location.href);
  const path = destination.pathname;
  const search = destination.searchParams.toString();

  if (destination.origin !== location.origin) return { kind: "finish" };

  if (current?.path === path && current.search === search)
    return { kind: "unchanged" };

  const localUpdate =
    load === "soft" && (path === committed.path || path === current?.path);
  const target = targetAt({ path, search });

  if (!target) return { kind: "finish" };

  if (continuesVisit({ current, navigation, localUpdate, target }))
    return { kind: "redirect", target };

  return localUpdate ? { kind: "finish" } : { kind: "start", target };
}

export function createVisit({
  target,
  committed,
  navigation,
  started,
  load,
  redirected,
}: {
  readonly target: Target;
  readonly committed: Address;
  readonly navigation: NavigationStart["navigation"];
  readonly started: number;
  readonly load: Visit["load"];
  readonly redirected: boolean;
}): Visit {
  return {
    ...target,
    id: crypto.randomUUID(),
    sourcePath: committed.path,
    sourceSearch: committed.search,
    started,
    navigation,
    load,
    foreground: document.visibilityState === "visible",
    redirected: redirected || target.family !== matchRoute(target.path).name,
    settled: new Set(),
    overdue: new Set(),
  };
}
